import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

const RefundPolicyPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const sections = [
    {
      id: 'overview',
      title: 'Overview',
      content: `VORENN TECH is committed to delivering exceptional IT consulting, cloud migration, business support, advertisement & marketing, and social media management services. This Refund Policy and Terms of Conditions document outlines the terms under which refunds may be requested and the general conditions governing the use of our services. By engaging with VORENN TECH, you acknowledge and agree to these terms.`
    },
    {
      id: 'refund-eligibility',
      title: 'Service Refund Eligibility',
      content: `Refunds are considered on a case-by-case basis and are subject to the following conditions:`,
      list: [
        'IT Consulting Services: Refunds may be issued if the initial consultation does not meet the agreed scope or deliverables within 30 days of service commencement.',
        'Cloud Migration Services: Partial refunds are available if migration milestones are not achieved due to our failure to deliver as per the project timeline, provided the claim is made within 14 days of the missed milestone.',
        'Business Support Services: Refunds for ongoing support packages may be prorated if services are canceled within the first 30 days and less than 25% of the contracted hours have been utilized.',
        'Advertisement & Marketing Services: Campaign-based services are eligible for partial refunds if performance metrics fall below 50% of agreed KPIs within the first 30 days, subject to verification.',
        'SMM Management Services: Monthly retainer refunds are available if content deliverables are not met as per the agreed schedule within the first billing cycle.'
      ]
    },
    {
      id: 'non-refundable',
      title: 'Non-Refundable Items',
      content: `The following are not eligible for refunds:`,
      list: [
        'Third-party software licenses, subscriptions, or cloud infrastructure costs incurred on your behalf',
        'Custom development work that has been completed and delivered',
        'Services rendered beyond the refund eligibility period',
        'Marketing campaigns that have already been launched and ad spend has been allocated',
        'Social media content that has been published or scheduled',
        'Consultation fees for services that have been fully delivered'
      ]
    },
    {
      id: 'refund-process',
      title: 'Refund Process & Timeline',
      content: `To request a refund, please follow these steps:`,
      list: [
        'Submit a written refund request to support@vorenntech.com within the eligible timeframe specified above',
        'Include your service agreement number, detailed reason for the refund request, and supporting documentation',
        'Our team will review your request within 5-7 business days',
        'If approved, refunds will be processed within 14-21 business days to the original payment method',
        'You will receive email confirmation once the refund has been initiated'
      ]
    },
    {
      id: 'cancellation',
      title: 'Cancellation Policy',
      content: `Service cancellations are subject to the following terms:`,
      list: [
        'Project-based services: Cancellations must be submitted in writing at least 48 hours before the scheduled start date to avoid charges',
        'Monthly retainer services: Cancellations require 30 days written notice. You will be billed for the current month plus any outstanding deliverables',
        'Ongoing support contracts: Early termination fees may apply as outlined in your service agreement',
        'Advertisement campaigns: Cancellations after campaign launch will not be refunded for ad spend already allocated'
      ]
    },
    {
      id: 'dispute-resolution',
      title: 'Dispute Resolution',
      content: `In the event of a dispute regarding services or refunds:`,
      list: [
        'Contact our support team at support@vorenntech.com to initiate a formal complaint',
        'We will work with you in good faith to resolve the issue within 15 business days',
        'If a resolution cannot be reached, both parties agree to pursue mediation before legal action',
        'All disputes will be governed by the laws of the jurisdiction in which VORENN TECH operates'
      ]
    }
  ];

  const termsSection = [
    {
      id: 'service-usage',
      title: 'Service Usage Terms',
      list: [
        'You agree to provide accurate information and timely access to necessary resources for service delivery',
        'VORENN TECH reserves the right to refuse or terminate services if terms are violated',
        'You are responsible for maintaining the confidentiality of any login credentials provided',
        'Misuse of our services, including fraudulent activity, will result in immediate termination without refund'
      ]
    },
    {
      id: 'payment-terms',
      title: 'Payment Terms',
      list: [
        'All invoices are due within 15 days of issuance unless otherwise agreed in writing',
        'Late payments may incur a fee of 1.5% per month on the outstanding balance',
        'Project-based services require a 50% deposit before work commences',
        'Monthly retainer services are billed in advance on the 1st of each month',
        'We accept credit cards, debit cards, and PayPal as payment methods'
      ]
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      list: [
        'VORENN TECH will not be liable for indirect, incidental, or consequential damages arising from service use',
        'Our total liability is limited to the amount paid for the specific service in question',
        'We are not responsible for third-party service failures, including cloud providers or advertising platforms',
        'Force majeure events beyond our control exempt us from liability for service delays or failures'
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      list: [
        'All deliverables created by VORENN TECH become your property upon full payment',
        'We retain the right to use project work as portfolio examples unless a non-disclosure agreement is in place',
        'You grant us a license to use your brand assets solely for the purpose of delivering contracted services',
        'Any pre-existing VORENN TECH intellectual property, tools, or methodologies remain our property'
      ]
    },
    {
      id: 'modifications',
      title: 'Modifications to Terms',
      list: [
        'VORENN TECH reserves the right to update these terms at any time',
        'Changes will be posted on this page with an updated "Last Modified" date',
        'Continued use of our services after changes constitutes acceptance of the new terms',
        'For significant changes, we will notify active clients via email'
      ]
    }
  ];

  return (
    <>
      <SeoHead
        title="Refund Policy & Terms of Conditions | VORENN TECH"
        description="Review VORENN TECH's refund policy and terms of conditions for IT consulting, cloud migration, business support, advertisement & marketing, and SMM management services. Understand eligibility, cancellation policies, and service terms."
        keywords="refund policy, terms of conditions, VORENN TECH terms, service agreement, cancellation policy, IT services terms"
        canonical="https://vorenntech.com/refund-policy"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <div className="pt-24 pb-16 bg-gradient-to-br from-teal-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                Refund Policy & Terms of Conditions
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className="space-y-12">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <span className="w-1.5 h-8 bg-cyan-500 rounded-full mr-4"></span>
                  {section.title}
                </h2>
                <div className="pl-6">
                  <p className="text-base text-slate-700 leading-relaxed mb-4">
                    {section.content}
                  </p>
                  {section.list && (
                    <ul className="space-y-3">
                      {section.list.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-fill text-cyan-500 text-lg mt-0.5 mr-3 flex-shrink-0"></i>
                          <span className="text-base text-slate-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}

            <div id="terms-and-conditions" className="border-t-2 border-slate-200 pt-12 mt-12 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
                Terms & Conditions
              </h2>
              <div className="space-y-10">
                {termsSection.map((term) => (
                  <section key={term.id} id={term.id} className="scroll-mt-24">
                    <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                      <span className="w-1 h-6 bg-teal-500 rounded-full mr-3"></span>
                      {term.title}
                    </h3>
                    <ul className="space-y-3 pl-5">
                      {term.list.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-arrow-right-s-fill text-teal-600 text-lg mt-0.5 mr-2 flex-shrink-0"></i>
                          <span className="text-base text-slate-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 mt-12 border border-teal-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-full flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Questions or Concerns?
                  </h3>
                  <p className="text-base text-slate-700 mb-4">
                    If you have any questions about our refund policy or terms of conditions, please don't hesitate to reach out to our support team.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href="mailto:support@vorenntech.com"
                      className="inline-flex items-center px-5 py-2.5 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-mail-line mr-2"></i>
                      Email Support
                    </a>
                    <a
                      href="/contact"
                      className="inline-flex items-center px-5 py-2.5 bg-white text-cyan-600 text-sm font-semibold rounded-lg border-2 border-cyan-500 hover:bg-cyan-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-message-3-line mr-2"></i>
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center pt-8 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                By using VORENN TECH services, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions.
              </p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default RefundPolicyPage;